import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { JobDetailModal } from '../../../../components/JobDetailView';
import { getUroSocket } from '../../../../services/URO';
import agentActions from '../../../../actions/agent';

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Manages all data logic for the generations panel.
 * Keeps panel display state (panelImages) strictly separate from the shared
 * completedImages state so ConversationChat inline results are never wiped.
 */
function useGenerationsPanel({ threadId, threadBrandId, pendingJobIds, setCompletedImages }) {
  const [filterMode, setFilterMode] = useState('conversation');
  const [panelImages, setPanelImages] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const fetchedJobIdsRef = useRef(new Set());
  const pollingIntervalsRef = useRef({});

  // ── Helpers ──────────────────────────────────────────────────────────────

  const addEntry = useCallback((jobId, entry) => {
    setPanelImages(prev => ({ ...prev, [jobId]: entry }));
    setCompletedImages(prev => ({ ...prev, [jobId]: entry }));
  }, [setCompletedImages]);

  const stopPolling = useCallback((jobId) => {
    if (pollingIntervalsRef.current[jobId]) {
      clearInterval(pollingIntervalsRef.current[jobId]);
      delete pollingIntervalsRef.current[jobId];
    }
  }, []);

  // ── API fetch ─────────────────────────────────────────────────────────────

  const fetchJobs = useCallback((mode, tId, bId) => {
    if (mode === 'conversation' && !tId) { setPanelImages({}); return; }
    if (mode === 'brand' && !bId) { setPanelImages({}); return; }

    const params = mode === 'brand' ? { brandId: bId } : { threadId: tId };
    setIsLoading(true);
    setPanelImages({});

    agentActions.listBrandImageJobs({ ...params, limit: 100 })
      .then(response => {
        if (!response) { return; }
        // Response shape: { success, data: { items: [...], pagination: {...} } }
        const jobs = response?.data?.items || [];
        const images = {};
        for (const job of jobs) {
          if (job.results?.length > 0) {
            const urls = job.results
              .map(r => r.external_url || r.content || r.url)
              .filter(Boolean);
            if (urls.length > 0) {
              images[job.image_job_id] = { ...job, urls, status: 'completed' };
            }
          }
        }
        setPanelImages(images);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  // ── Thread change: reset and reload ──────────────────────────────────────

  useEffect(() => {
    setFilterMode('conversation');
    fetchedJobIdsRef.current = new Set();
    fetchJobs('conversation', threadId, threadBrandId);
  }, [threadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chip switch ───────────────────────────────────────────────────────────

  const handleChipChange = useCallback((mode) => {
    setFilterMode(mode);
    fetchedJobIdsRef.current = new Set();
    fetchJobs(mode, threadId, threadBrandId);
  }, [threadId, threadBrandId, fetchJobs]);

  // ── WebSocket: real-time completion ───────────────────────────────────────

  useEffect(() => {
    const socket = getUroSocket();

    const handleComplete = (data) => {
      const jobId = data.image_job_id || data.workflow_job_id;
      if (!jobId) return;
      stopPolling(jobId);
      const raw = data.results;
      const resultsArr = Array.isArray(raw)
        ? raw
        : raw && typeof raw === 'object' ? Object.values(raw) : [];
      const urls = resultsArr
        .map(r => (typeof r === 'string' ? r : r?.external_url || r?.content || r?.url))
        .filter(Boolean);
      const jobType = data.image_job_id ? 'image' : 'workflow';
      addEntry(jobId, { ...data, image_job_id: jobId, urls, status: 'completed', jobType });
    };

    const handleError = (data) => {
      const jobId = data.image_job_id || data.workflow_job_id;
      if (!jobId) return;
      stopPolling(jobId);
      addEntry(jobId, { ...data, image_job_id: jobId, urls: [], status: 'failed', error: data.error || 'Failed' });
    };

    socket.on('job:complete', handleComplete);
    socket.on('job:error', handleError);
    socket.on('workflow:complete', handleComplete);
    socket.on('workflow:failed', handleError);

    return () => {
      socket.off('job:complete', handleComplete);
      socket.off('job:error', handleError);
      socket.off('workflow:complete', handleComplete);
      socket.off('workflow:failed', handleError);
      Object.values(pollingIntervalsRef.current).forEach(clearInterval);
      pollingIntervalsRef.current = {};
    };
  }, [addEntry, stopPolling]);

  // ── Pending job polling ────────────────────────────────────────────────────

  useEffect(() => {
    if (!pendingJobIds.length) return;

    const pollJob = (jobId) => {
      if (pollingIntervalsRef.current[jobId]) return;
      const interval = setInterval(async () => {
        try {
          const d = await agentActions.getImageJob(jobId);
          const job = d?.data;
          if (!job) return;
          if (job.status === 'completed' && job.results?.length > 0) {
            const urls = job.results.map(r => r.external_url || r.content || r.url).filter(Boolean);
            if (urls.length > 0) addEntry(jobId, { ...job, image_job_id: jobId, urls, status: 'completed' });
            stopPolling(jobId);
          } else if (job.status === 'failed') {
            addEntry(jobId, { ...job, image_job_id: jobId, urls: [], status: 'failed', error: job.failed_message || 'Failed' });
            stopPolling(jobId);
          }
        } catch (_) {}
      }, 4000);
      pollingIntervalsRef.current[jobId] = interval;
    };

    const fetchInitial = async (jobId) => {
      try {
        const d = await agentActions.getImageJob(jobId);
        const job = d?.data;
        if (!job) return;

        if (job.status === 'completed' && job.results?.length > 0) {
          const urls = job.results
            .map(r => r.external_url || r.content || r.url)
            .filter(Boolean);
          if (urls.length > 0) addEntry(jobId, { ...job, image_job_id: jobId, urls, status: 'completed' });
        } else if (job.status === 'failed') {
          addEntry(jobId, { ...job, image_job_id: jobId, urls: [], status: 'failed', error: job.failed_message || 'Failed' });
        } else if (job.status === 'processing' || job.status === 'pending') {
          setPanelImages(prev => ({ ...prev, [jobId]: { image_job_id: jobId, status: job.status } }));
          pollJob(jobId);
        }
      } catch (_) {}
    };

    pendingJobIds.forEach(jobId => {
      if (fetchedJobIdsRef.current.has(jobId)) return;
      fetchedJobIdsRef.current.add(jobId);
      fetchInitial(jobId);
    });
  }, [pendingJobIds, addEntry, stopPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  const jobs = useMemo(() =>
    Object.values(panelImages)
      .filter(j => j?.status || j?.urls?.length > 0)
      .sort((a, b) => {
        const ta = new Date(b.created_at || b.completed_at || 0).getTime();
        const tb = new Date(a.created_at || a.completed_at || 0).getTime();
        return ta - tb;
      }),
    [panelImages]
  );

  return { jobs, isLoading, filterMode, handleChipChange };
}

// ─── GenCard ───────────────────────────────────────────────────────────────────

function GenCard({ job, onClick, onUseAsInput }) {
  const firstResult = Array.isArray(job?.results)
    ? job.results.find(r => r?.external_url || r?.content)
    : null;
  const mediaUrl = firstResult?.external_url || firstResult?.content;
  const isVideo = firstResult?.content_type === 'video';
  const isText = firstResult?.content_type === 'text';
  const isImage = firstResult && !isVideo && !isText;
  const isPending = job?.status === 'processing' || job?.status === 'pending';
  const isFailed = job?.status === 'failed';

  const handleUseAsInput = (e) => {
    e.stopPropagation();
    if (mediaUrl && onUseAsInput) onUseAsInput(mediaUrl);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (mediaUrl) window.open(mediaUrl, '_blank');
  };

  return (
    <div
      className="gen-card"
      onClick={() => onClick(job)}
      style={{
        position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden',
        cursor: 'pointer', background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        transition: 'border-color 150ms ease',
      }}
    >
      {isImage && mediaUrl ? (
        <img src={mediaUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : isVideo && mediaUrl ? (
        <video src={mediaUrl} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : isText ? (
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(59,130,246,0.12))', display: 'flex', alignItems: 'center', padding: 6 }}>
          <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.75)', lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical' }}>
            {firstResult?.content}
          </span>
        </div>
      ) : isPending ? (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(56,189,248,0.3)', borderTopColor: '#38BDF8', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : isFailed ? (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,77,79,0.08)' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        </div>
      ) : null}

      {/* Hover overlay with actions */}
      {(isImage || isVideo) && (
        <div className="gen-card-overlay">
          <button className="gen-card-action" onClick={handleDownload} title="Download" aria-label="Download image">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
          <button className="gen-card-action" onClick={handleUseAsInput} title="Use as input" aria-label="Use as input">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
          </button>
          <button className="gen-card-action" onClick={(e) => { e.stopPropagation(); onClick(job); }} title="View details" aria-label="View details">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── FilterChip ────────────────────────────────────────────────────────────────

function FilterChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
        cursor: 'pointer', transition: 'all 140ms ease', fontFamily: 'inherit',
        border: active ? '1px solid rgba(255,126,103,0.4)' : '1px solid rgba(255,255,255,0.08)',
        background: active ? 'rgba(255,126,103,0.15)' : 'rgba(255,255,255,0.04)',
        color: active ? '#FF7E67' : 'rgba(255,255,255,0.35)',
      }}
    >
      {label}
    </button>
  );
}

// ─── Empty / Loading states ────────────────────────────────────────────────────

function EmptyState({ isLoading, filterMode, hasThread }) {
  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160 }}>
        <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid rgba(56,189,248,0.25)', borderTopColor: '#38BDF8', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }
  const msg = !hasThread
    ? 'Select a conversation to see generations'
    : filterMode === 'brand'
      ? 'No brand generations yet'
      : 'No generations in this conversation';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 160, gap: 10, color: 'rgba(255,255,255,0.15)', textAlign: 'center' }}>
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
        <path d="M12 2l2 6h6l-5 3.5 2 6L12 14l-5 3.5 2-6L4 8h6z" />
      </svg>
      <span style={{ fontSize: 12, fontWeight: 500 }}>{msg}</span>
    </div>
  );
}

// ─── AssetPanel ────────────────────────────────────────────────────────────────

export default function AssetPanel({ setCompletedImages, pendingJobIds = [], threadId, threadBrandId, onUseAsInput }) {
  const [selectedJob, setSelectedJob] = useState(null);
  const { jobs, isLoading, filterMode, handleChipChange } = useGenerationsPanel({
    threadId,
    threadBrandId,
    pendingJobIds,
    setCompletedImages,
  });

  const completedCount = jobs.filter(j => j.status === 'completed' && j.urls?.length > 0).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'rgba(255,255,255,0.02)', borderLeft: '1px solid rgba(255,255,255,0.10)' }}>

      {/* Header */}
      <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
            </svg>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.03em' }}>
              Generations
            </span>
          </div>
          {completedCount > 0 && (
            <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(255,126,103,0.15)', color: '#FF7E67', borderRadius: 10, padding: '2px 8px', lineHeight: '14px' }}>
              {completedCount}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <FilterChip label="Conversation" active={filterMode === 'conversation'} onClick={() => handleChipChange('conversation')} />
          <FilterChip label="Brand" active={filterMode === 'brand'} onClick={() => handleChipChange('brand')} />
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
        {jobs.length === 0 ? (
          <EmptyState isLoading={isLoading} filterMode={filterMode} hasThread={!!threadId} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {jobs.map((job, i) => (
              <GenCard key={job.image_job_id || job.workflow_job_id || i} job={job} onClick={setSelectedJob} onUseAsInput={onUseAsInput} />
            ))}
          </div>
        )}
      </div>

      <JobDetailModal visible={!!selectedJob} data={selectedJob} onClose={() => setSelectedJob(null)} />
    </div>
  );
}
